import React from 'react';
import { motion } from 'framer-motion';
import { 
  Eye, 
  CheckCircle, 
  Circle, 
  Square, 
  Type,
  ArrowUpDown,
  Link
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { RadioGroup, RadioGroupItem } from '../../ui/radio-group';
import { Checkbox } from '../../ui/checkbox';

interface QuestionPreviewProps {
  questionText: string;
  questionType: 'multiple_choice' | 'single_choice' | 'true_false' | 'text_input' | 'matching' | 'ordering';
  points: number;
  explanation?: string;
  isRequired: boolean;
  answers: {
    answer_text: string;
    is_correct: boolean;
  }[];
}

const QuestionPreview: React.FC<QuestionPreviewProps> = ({
  questionText,
  questionType,
  points,
  explanation,
  isRequired,
  answers
}) => {
  const getQuestionTypeIcon = (type: string) => {
    switch (type) {
      case 'single_choice': return <Circle size={16} className="text-blue-400" />;
      case 'multiple_choice': return <Square size={16} className="text-green-400" />;
      case 'true_false': return <CheckCircle size={16} className="text-yellow-400" />;
      case 'text_input': return <Type size={16} className="text-purple-400" />;
      case 'ordering': return <ArrowUpDown size={16} className="text-orange-400" />;
      case 'matching': return <Link size={16} className="text-pink-400" />;
      default: return <Circle size={16} className="text-gray-400" />;
    }
  };

  const getQuestionTypeLabel = (type: string) => {
    const types = {
      single_choice: 'Choix unique',
      multiple_choice: 'Choix multiple',
      true_false: 'Vrai/Faux',
      text_input: 'Texte libre',
      ordering: 'Classement',
      matching: 'Appariement'
    };
    return types[type as keyof typeof types] || type;
  };

  const renderAnswerOptions = () => {
    if (!answers || answers.length === 0) {
      return (
        <div className="text-center py-8 text-gray-400">
          <Circle size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">Aucune réponse définie</p>
        </div>
      );
    }

    switch (questionType) {
      case 'single_choice':
        return (
          <RadioGroup className="space-y-3">
            {answers.map((answer, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors ${
                  answer.is_correct 
                    ? 'bg-green-500/10 border-green-500/30' 
                    : 'bg-gray-700/30 border-gray-600/30'
                }`}
              >
                <RadioGroupItem 
                  value={index.toString()} 
                  id={`radio-${index}`}
                  className="text-blue-400"
                />
                <label 
                  htmlFor={`radio-${index}`} 
                  className="text-white cursor-pointer flex-1"
                >
                  {answer.answer_text || `Réponse ${index + 1}`}
                </label>
                {answer.is_correct && (
                  <Badge variant="outline" className="border-green-500/30 text-green-400 bg-green-500/10 text-xs">
                    Correcte
                  </Badge>
                )}
              </motion.div>
            ))}
          </RadioGroup>
        );

      case 'multiple_choice':
        return (
          <div className="space-y-3">
            {answers.map((answer, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors ${
                  answer.is_correct 
                    ? 'bg-green-500/10 border-green-500/30' 
                    : 'bg-gray-700/30 border-gray-600/30'
                }`}
              >
                <Checkbox 
                  id={`checkbox-${index}`}
                  className="text-green-400"
                />
                <label 
                  htmlFor={`checkbox-${index}`} 
                  className="text-white cursor-pointer flex-1"
                >
                  {answer.answer_text || `Réponse ${index + 1}`}
                </label>
                {answer.is_correct && (
                  <Badge variant="outline" className="border-green-500/30 text-green-400 bg-green-500/10 text-xs">
                    Correcte
                  </Badge>
                )}
              </motion.div>
            ))}
          </div>
        );

      case 'true_false':
        return (
          <RadioGroup className="space-y-3">
            {['Vrai', 'Faux'].map((option, index) => {
              const isCorrect = (index === 0 && answers[0]?.is_correct) || (index === 1 && answers[1]?.is_correct);
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors ${
                    isCorrect 
                      ? 'bg-green-500/10 border-green-500/30' 
                      : 'bg-gray-700/30 border-gray-600/30'
                  }`}
                >
                  <RadioGroupItem 
                    value={index.toString()} 
                    id={`tf-${index}`}
                    className="text-yellow-400"
                  />
                  <label 
                    htmlFor={`tf-${index}`} 
                    className="text-white cursor-pointer flex-1"
                  >
                    {option}
                  </label>
                  {isCorrect && (
                    <Badge variant="outline" className="border-green-500/30 text-green-400 bg-green-500/10 text-xs">
                      Correcte
                    </Badge>
                  )}
                </motion.div>
              );
            })}
          </RadioGroup>
        );

      case 'text_input':
        return (
          <div className="space-y-3">
            <Textarea
              placeholder="L'étudiant saisira sa réponse ici..."
              className="bg-gray-700/30 border-gray-600/30 text-white placeholder-gray-400 resize-none"
              rows={3}
              disabled
            />
            <div className="text-xs text-gray-400">
              <strong>Note :</strong> Les réponses textuelles seront évaluées manuellement ou par mots-clés.
            </div>
          </div>
        );

      case 'ordering':
        return (
          <div className="space-y-3">
            {answers.map((answer, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center space-x-3 p-3 bg-gray-700/30 border border-gray-600/30 rounded-lg"
              >
                <div className="w-6 h-6 bg-orange-500/20 rounded border border-orange-500/30 flex items-center justify-center">
                  <ArrowUpDown size={12} className="text-orange-400" />
                </div>
                <span className="text-white flex-1">
                  {answer.answer_text || `Élément ${index + 1}`}
                </span>
                <Badge variant="outline" className="border-orange-500/30 text-orange-400 bg-orange-500/10 text-xs">
                  Position {index + 1}
                </Badge>
              </motion.div>
            ))}
            <div className="text-xs text-gray-400">
              <strong>Note :</strong> L'étudiant devra glisser-déposer les éléments dans le bon ordre.
            </div>
          </div>
        );

      case 'matching':
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-gray-300 mb-2">Éléments à associer</h4>
                {answers.slice(0, Math.ceil(answers.length / 2)).map((answer, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="p-2 bg-pink-500/10 border border-pink-500/30 rounded mb-2"
                  >
                    <span className="text-white text-sm">
                      {answer.answer_text || `Élément ${index + 1}`}
                    </span>
                  </motion.div>
                ))}
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-300 mb-2">Correspondances</h4>
                {answers.slice(Math.ceil(answers.length / 2)).map((answer, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="p-2 bg-pink-500/10 border border-pink-500/30 rounded mb-2"
                  >
                    <span className="text-white text-sm">
                      {answer.answer_text || `Correspondance ${index + 1}`}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>
            <div className="text-xs text-gray-400">
              <strong>Note :</strong> L'étudiant devra associer les éléments de gauche avec ceux de droite.
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center py-4 text-gray-400">
            <p className="text-sm">Type de question non supporté pour la prévisualisation</p>
          </div>
        );
    }
  };

  return (
    <Card className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700">
      <CardHeader className="pb-3">
        <CardTitle className="text-white flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Eye className="w-5 h-5 text-cyan-400" />
            <span>Prévisualisation</span>
          </div>
          <div className="flex items-center space-x-2">
            {getQuestionTypeIcon(questionType)}
            <Badge variant="outline" className="border-cyan-500/30 text-cyan-400 bg-cyan-500/10">
              {getQuestionTypeLabel(questionType)}
            </Badge>
            <Badge variant="outline" className="border-purple-500/30 text-purple-400 bg-purple-500/10">
              {points} pt{points > 1 ? 's' : ''}
            </Badge>
            {isRequired && (
              <Badge variant="outline" className="border-yellow-500/30 text-yellow-400 bg-yellow-500/10">
                Obligatoire
              </Badge>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Question */}
        <div className="bg-gray-700/30 p-4 rounded-lg border border-gray-600/30">
          <h3 className="text-white font-medium leading-relaxed mb-3">
            {questionText || (
              <span className="text-gray-400 italic">
                Saisissez le texte de votre question...
              </span>
            )}
          </h3>
          
          {/* Réponses */}
          <div className="mt-4">
            {renderAnswerOptions()}
          </div>
        </div>

        {/* Explication */}
        {explanation && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-blue-500/10 border border-blue-500/30 p-3 rounded-lg"
          >
            <div className="flex items-start space-x-2">
              <div className="w-5 h-5 bg-blue-500/20 rounded-full flex items-center justify-center mt-0.5">
                <span className="text-blue-400 text-xs">i</span>
              </div>
              <div>
                <h4 className="text-blue-400 text-sm font-medium mb-1">Explication</h4>
                <p className="text-blue-200 text-sm leading-relaxed">{explanation}</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Indicateurs de validation */}
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center space-x-4">
            <span className={`flex items-center space-x-1 ${questionText ? 'text-green-400' : 'text-gray-400'}`}>
              <Circle size={8} className={questionText ? 'fill-current' : ''} />
              <span>Question définie</span>
            </span>
            <span className={`flex items-center space-x-1 ${answers.length > 0 ? 'text-green-400' : 'text-gray-400'}`}>
              <Circle size={8} className={answers.length > 0 ? 'fill-current' : ''} />
              <span>Réponses ajoutées</span>
            </span>
            {['single_choice', 'multiple_choice', 'true_false'].includes(questionType) && (
              <span className={`flex items-center space-x-1 ${answers.some(a => a.is_correct) ? 'text-green-400' : 'text-red-400'}`}>
                <Circle size={8} className={answers.some(a => a.is_correct) ? 'fill-current' : ''} />
                <span>Bonne réponse définie</span>
              </span>
            )}
          </div>
          <div className="text-gray-400">
            Aperçu étudiant
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export { QuestionPreview };
